// src/pages/doctors/patients/DoctorPastAppointments.jsx //
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { supabase } from "@/services/supabaseClient";
import { loadDoctorAppointmentsFeed } from "@/pages/doctors/appointments/lib/doctorAppointmentsData.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { pickDiagnosisTitle } from "@/utils/diagnosis.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";

const ICON_YEARS = "/icons/appointments/years_experience.svg";
const ICON_STAR = "/icons/appointments/star.svg";
const ICON_VERIFIED = "/icons/doctor/appointments/verified.svg";
const ICON_PENDING_VERIFICATION = "/icons/pending-verification.svg";

function safeDate(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d;
}

function snapshotStartDate(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return null;

  const datePart = String(snapshot?.dateISO || snapshot?.selectedDate || "").slice(0, 10);
  const timePart = String(snapshot?.start || snapshot?.selectedTime || "").trim();
  if (datePart) {
    const maybe = new Date(`${datePart}T${timePart || "00:00"}:00`);
    if (!Number.isNaN(maybe.getTime())) return maybe;
  }

  return safeDate(snapshot?.appointment_date);
}

function snapshotEndDate(snapshot) {
  const start = snapshotStartDate(snapshot);
  if (!start) return null;

  const durationMinutes = Math.max(
    0,
    Number(snapshot?.duration_minutes || snapshot?.durationMinutes || snapshot?.duration || 0)
  );
  return new Date(start.getTime() + durationMinutes * 60 * 1000);
}

function byDescDateTime(a, b) {
  const aTs = snapshotStartDate(a?.payload || a)?.getTime() || 0;
  const bTs = snapshotStartDate(b?.payload || b)?.getTime() || 0;
  return bTs - aTs;
}

function formatSectionDate(item, locale = "en-US", fallback = "Unknown date") {
  const d = snapshotStartDate(item?.payload || item);
  if (!d) return fallback;
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(d);
}

function toDayKey(item) {
  const snapshot = item?.payload || item;
  const datePart = String(snapshot?.dateISO || snapshot?.selectedDate || "").slice(0, 10);
  if (datePart) return datePart;

  const d = snapshotStartDate(snapshot);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function pickFirstText(...values) {
  for (const value of values) {
    if (Array.isArray(value)) {
      const joined = value.map((item) => String(item || "").trim()).filter(Boolean).join(", ");
      if (joined) return joined;
      continue;
    }

    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function normalizeDoctorDisplayName(name, fallback = "Doctor") {
  const resolved = resolvePatientFacingDoctorName(name);
  return resolved === "Doctor" ? fallback : resolved;
}

async function resolveDoctorAvatar(client, primaryUrl, photoPath, fallback = "/images/demo/doctors/avatar-fallback.jpg") {
  const direct = String(primaryUrl || "").trim();
  if (direct) return direct;

  const cleanPath = String(photoPath || "").trim();
  if (cleanPath) {
    if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
    try {
      const { data, error } = await client.storage
        .from("doctor-profile-images")
        .createSignedUrl(cleanPath, 60 * 60 * 24);
      if (!error && data?.signedUrl) return data.signedUrl;
    } catch {
      // fall through
    }
  }

  return fallback;
}

function isWeakDoctorValue(value, weakValues = []) {
  const text = String(value || "").trim().toLowerCase();
  return !text || weakValues.includes(text);
}

function doctorSpecialtyText(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean).join(", ");
  }
  return String(value || "").trim();
}

function pickDoctorName(labels, ...candidates) {
  for (const candidate of candidates) {
    const resolved = normalizeDoctorDisplayName(candidate, labels.doctorFallback || "Doctor");
    if (!isWeakDoctorValue(resolved, ["doctor"])) return resolved;
  }
  return labels.doctorFallback || "Doctor";
}

function pickDoctorSpecialty(labels, ...candidates) {
  for (const candidate of candidates) {
    const text = doctorSpecialtyText(candidate);
    if (!isWeakDoctorValue(text, ["doctor", "general practitioner", "dermatologist"])) {
      return text;
    }
  }
  return labels.specialtyFallback || "Specialist";
}

function pickDoctorMetric(...candidates) {
  for (const candidate of candidates) {
    const text = String(candidate ?? "").trim();
    if (text && text !== "—") return text;
  }
  return "—";
}

function loadAppointmentDraft(appointmentId) {
  if (typeof window === "undefined") return null;

  const key = `afrimed:doctor_appt:${String(appointmentId)}`;

  try {
    const sessionRaw = window.sessionStorage.getItem(key);
    if (sessionRaw) return JSON.parse(sessionRaw);
  } catch {
    // ignore
  }

  try {
    const localRaw = window.localStorage.getItem(key);
    return localRaw ? JSON.parse(localRaw) : null;
  } catch {
    return null;
  }
}

function deriveDiagnosis(item, fallback = "No diagnosis recorded") {
  const appointmentId = String(item?.appointmentId || item?.id || "").trim();
  const appointmentDraft = appointmentId ? loadAppointmentDraft(appointmentId) : null;
  const snapshot = item?.payload || {};

  return (
    pickDiagnosisTitle(
      appointmentDraft?.diagnosisTitle,
      appointmentDraft?.diagnosis,
      snapshot?.diagnosisTitle,
      snapshot?.diagnosis,
    ) || fallback
  );
}

function resolveDoctorDisplay(fallbackPatient, fallbackDoctor, publicDoctor, snapshotDoctor, labels = {}) {
  const name = pickDoctorName(
    labels,
    snapshotDoctor?.name,
    publicDoctor?.display_name,
    fallbackDoctor?.display_name,
    fallbackDoctor?.full_name,
    fallbackPatient?.doctor_name
  );

  const avatar =
    String(
      snapshotDoctor?.avatar ||
        fallbackDoctor?.profile_image_url ||
        fallbackDoctor?.resolved_avatar_url ||
        fallbackDoctor?.avatar_url ||
        fallbackPatient?.doctor_avatar ||
        ""
    ).trim() || "/images/demo/doctors/avatar-fallback.jpg";

  const specialty = pickDoctorSpecialty(
    labels,
    snapshotDoctor?.specialty,
    publicDoctor?.specialty,
    fallbackDoctor?.specialization,
    fallbackDoctor?.specialty,
    fallbackPatient?.doctor_specialty
  );

  const years = pickDoctorMetric(
    snapshotDoctor?.yearsExperience,
    publicDoctor?.years_of_experience,
    fallbackDoctor?.years_experience,
    fallbackDoctor?.years_of_experience,
    fallbackPatient?.doctor_years
  );
  const rating = pickDoctorMetric(
    snapshotDoctor?.rating,
    fallbackDoctor?.rating,
    fallbackPatient?.doctor_rating
  );
  const verificationStatus = normalizeVerificationStatus(
    snapshotDoctor?.verificationStatus ||
      publicDoctor?.verified_status ||
      fallbackDoctor?.verified_status,
    snapshotDoctor?.verified ? "verified" : "pending"
  );

  return {
    name,
    avatar,
    specialty,
    years,
    rating,
    verificationStatus: verificationStatus || (snapshotDoctor?.verified ? "verified" : ""),
    verified:
      verificationStatus === "verified" ||
      Boolean(snapshotDoctor?.verified),
  };
}

async function resolvePatientRowIds(patientRef) {
  const candidate = String(patientRef || "").trim();
  if (!candidate) return [];

  const ids = new Set([candidate]);

  const [{ data: byUser }, { data: byRow }] = await Promise.all([
    supabase.from("patients").select("id").eq("user_id", candidate).maybeSingle(),
    supabase.from("patients").select("id").eq("id", candidate).maybeSingle(),
  ]);

  if (byUser?.id) ids.add(String(byUser.id));
  if (byRow?.id) ids.add(String(byRow.id));

  return Array.from(ids).filter(Boolean);
}

function matchesPatientSnapshot(snapshot, candidateIds) {
  const ids = [
    snapshot?.patientId,
    snapshot?.patient_id,
    snapshot?.patient_user_id,
    snapshot?.patient_row_id,
    snapshot?.patient?.id,
    snapshot?.patient?.user_id,
    snapshot?.patient?.patient_id,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  return ids.some((id) => candidateIds.has(id));
}

function isPastSnapshot(snapshot) {
  if (String(snapshot?.status || "").trim().toLowerCase() !== "completed") return false;
  const end = snapshotEndDate(snapshot);
  return end ? end.getTime() <= Date.now() : false;
}

export default function DoctorPastAppointments() {
  const nav = useNavigate();
  const location = useLocation();
  const { patientId } = useParams();
  const { t, languageCode } = useLanguage();

  const state = location.state || {};
  const restored = state?.restored || null;
  const patient = state?.patient || restored?.patient || null;
  const appointmentId = state?.appointmentId || restored?.appointmentId || null;

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [doctorDisplay, setDoctorDisplay] = React.useState(null);

  React.useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError || !user?.id) {
          throw new Error("Authentication required.");
        }

        const doctorId = String(user.id);
        const patientRef =
          String(patientId || "").trim() ||
          String(state?.patientId || "").trim() ||
          String(restored?.patientId || "").trim() ||
          String(patient?.id || "").trim();

        const patientIdCandidates = await resolvePatientRowIds(patientRef);
        if (patientIdCandidates.length === 0) {
          if (!alive) return;
          setRows([]);
          setLoading(false);
          return;
        }

        const [{ items, error: feedError }, doctorProfileRes, authProfileRes, publicDoctorRes] = await Promise.all([
          loadDoctorAppointmentsFeed({ doctorId, includeCancelled: false, supabase }),
          supabase
            .from("doctor_profiles")
            .select("user_id,full_name,specialization,specialty,profile_image_url,profile_photo_path,verified_status,years_experience,years_of_experience,rating")
            .eq("user_id", doctorId)
            .limit(1)
            .maybeSingle(),
          supabase
            .from("profiles")
            .select("id,user_id,display_name,avatar_url")
            .eq("user_id", doctorId)
            .limit(1)
            .maybeSingle(),
          supabase
            .from("v_doctor_directory_browse")
            .select("doctor_user_id,display_name,specialty,verified_status,profile_photo_path,years_of_experience")
            .eq("doctor_user_id", doctorId)
            .limit(1)
            .maybeSingle(),
        ]);

        if (feedError) throw feedError;

        const patientIdSet = new Set(patientIdCandidates);
        const filteredRows = (Array.isArray(items) ? items : []).filter((item) => {
          const snapshot = item?.payload || null;
          if (!snapshot) return false;
          return isPastSnapshot(snapshot) && matchesPatientSnapshot(snapshot, patientIdSet);
        });

        const doctorProfile = doctorProfileRes?.data || null;
        const authProfile = authProfileRes?.data || null;
        const publicDoctor = publicDoctorRes?.data || null;
        const resolvedAvatarUrl = await resolveDoctorAvatar(
          supabase,
          doctorProfile?.profile_image_url || "",
          doctorProfile?.profile_photo_path || publicDoctor?.profile_photo_path,
          "/images/demo/doctors/avatar-fallback.jpg"
        );

        if (!alive) return;
        setDoctorDisplay({
          user_id: doctorId,
          full_name: doctorProfile?.full_name || null,
          display_name: publicDoctor?.display_name || authProfile?.display_name || null,
          specialization: publicDoctor?.specialty || doctorProfile?.specialization || doctorProfile?.specialty || null,
          profile_image_url: doctorProfile?.profile_image_url || resolvedAvatarUrl || null,
          resolved_avatar_url: resolvedAvatarUrl || null,
          profile_photo_path: doctorProfile?.profile_photo_path || publicDoctor?.profile_photo_path || null,
          avatar_url: authProfile?.avatar_url || null,
          verified_status: publicDoctor?.verified_status || doctorProfile?.verified_status || null,
          years_experience:
            publicDoctor?.years_of_experience || doctorProfile?.years_experience || doctorProfile?.years_of_experience || null,
          rating: doctorProfile?.rating || null,
        });
        setRows(filteredRows);
      } catch (e) {
        if (!alive) return;
        setError(
          e?.message ||
            t(
              "doctors.patientPastAppointments.errors.load",
              "Failed to load past appointments."
            )
        );
        setDoctorDisplay(null);
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    window.addEventListener("afrimed:appointments:changed", load);
    return () => {
      alive = false;
      window.removeEventListener("afrimed:appointments:changed", load);
    };
  }, [patientId, state?.patientId, restored?.patientId, patient?.id, appointmentId]);

  const grouped = React.useMemo(() => {
    const map = new Map();
    rows
      .slice()
      .sort(byDescDateTime)
      .forEach((row) => {
        const key = toDayKey(row);
        if (!key) return;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row);
      });

    return Array.from(map.entries()).map(([dayKey, dayRows]) => ({
      dayKey,
      label: formatSectionDate(
        dayRows?.[0],
        languageCode,
        t("doctors.patientPastAppointments.unknownDate", "Unknown date")
      ),
      rows: dayRows,
    }));
  }, [languageCode, rows, t]);

  return (
    <div data-hide-tabbar="true" className="min-h-[100dvh]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("doctors.patientPastAppointments.title", "Past Appointments")}
          onBack={() => nav(-1)}
        />

        <div className="px-4">
          {loading ? (
            <div className="pt-4 text-body-medium text-on-surface-var">
              {t(
                "doctors.patientPastAppointments.loading",
                "Loading past appointments..."
              )}
            </div>
          ) : null}

          {!loading && error ? (
            <div className="pt-4 text-body-medium text-[#b42318]">{error}</div>
          ) : null}

          {!loading && !error && grouped.length === 0 ? (
            <div className="pt-4 text-body-medium text-on-surface-var">
              {t(
                "doctors.patientPastAppointments.empty",
                "No past appointments found."
              )}
            </div>
          ) : null}

          {!loading && !error ? (
            <div className="pt-2 space-y-6">
              {grouped.map((section) => (
                <section key={section.dayKey}>
                  <div className="mb-2 text-title-medium text-on-surface">{section.label}</div>

                  <div className="space-y-3">
                    {section.rows.map((row) => {
                      const doctor = resolveDoctorDisplay(
                        patient,
                        doctorDisplay,
                        doctorDisplay,
                        row?.payload?.doctor,
                        {
                          doctorFallback: t(
                            "doctors.patientPastAppointments.doctorFallback",
                            "Doctor"
                          ),
                          specialtyFallback: t(
                            "doctors.patientPastAppointments.specialtyFallback",
                            "Specialist"
                          ),
                        }
                      );
                      const diagnosis = deriveDiagnosis(
                        row,
                        t(
                          "doctors.patientPastAppointments.diagnosisFallback",
                          "No diagnosis recorded"
                        )
                      );
                      const routePatientId =
                        patientId ||
                        state?.patientId ||
                        restored?.patientId ||
                        patient?.id ||
                        row?.payload?.patientId ||
                        row?.payload?.patient?.id ||
                        null;
                      const rowSnapshot = {
                        ...(restored || {}),
                        ...(row?.payload || {}),
                        patient: {
                          ...(restored?.patient || {}),
                          ...(row?.payload?.patient || {}),
                          ...(patient || {}),
                        },
                        patientProfile: {
                          ...(restored?.patientProfile || {}),
                          ...(row?.payload?.patientProfile || {}),
                          ...(patient || {}),
                        },
                      };

                      return (
                        <button
                          key={row.id}
                          type="button"
                          className="
                            w-full overflow-hidden rounded-[12px] bg-white
                            text-left shadow-[0_1px_2px_rgba(15,23,42,0.06)]
                            active:opacity-95
                          "
                          onClick={() =>
                            nav(
                              `/doctors/patients/${encodeURIComponent(
                                String(routePatientId || "")
                              )}/ehr/past-appointments/${encodeURIComponent(String(row.id))}`,
                              {
                                state: {
                                  from: "doctor-ehr-past",
                                  patientId: routePatientId,
                                  patient:
                                    rowSnapshot.patient ||
                                    patient ||
                                    row?.payload?.patient ||
                                    null,
                                  restored: rowSnapshot,
                                  appointmentId: row.id,
                                  pastAppointment: row?.payload || row,
                                  pastAppointmentSnapshot: row?.payload || null,
                                  pastDiagnosis: diagnosis,
                                },
                              }
                            )
                          }
                        >
                          <div className="px-4 py-3">
                            <div className="flex items-start gap-3">
                              <img
                                src={doctor.avatar}
                                alt=""
                                className="h-[68px] w-[68px] rounded-[8px] border border-white object-cover shadow-[0_0_0_1px_rgba(24,50,119,0.10)]"
                                draggable="false"
                              />

                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1">
                                  <div className="truncate text-title-medium text-on-surface">{doctor.name}</div>
                                  {doctor.verificationStatus === "verified" || doctor.verificationStatus === "pending" ? (
                                    <img
                                      src={
                                        doctor.verificationStatus === "verified"
                                          ? ICON_VERIFIED
                                          : ICON_PENDING_VERIFICATION
                                      }
                                      alt=""
                                      className="h-4 w-4 shrink-0"
                                      draggable="false"
                                    />
                                  ) : null}
                                </div>

                                <div className="text-body-medium text-on-surface-var">{doctor.specialty}</div>

                                <div className="mt-0.5 flex items-center gap-1 text-body-medium text-on-surface-var">
                                  <span>{doctor.years}</span>
                                  <img src={ICON_YEARS} alt="" className="h-[18px] w-[18px]" draggable="false" />
                                  <span className="opacity-70">•</span>
                                  <span>{doctor.rating}</span>
                                  <img src={ICON_STAR} alt="" className="h-[18px] w-[18px]" draggable="false" />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="border-t border-[rgb(var(--clr-outline-variant))] px-4 py-3">
                            <div className="text-label-medium text-on-surface-var">
                              {t("doctors.patientPastAppointments.diagnosisLabel", "Diagnosis")}
                            </div>
                            <div className="mt-0.5 text-body-large text-on-surface-var">{diagnosis}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
